import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'src/app/core/api/api.service';
import { ENGAGEMENTS_ENDPOINTS } from 'src/app/core/api/endpoints/engagements.endpoints';
import { QuestionAnswersQueryParams, QuestionAnswersResponse } from '../models/question-answer.model';
import { QuestionLogsResponse } from '../models/question-log.model';
import {
  DashboardSummaryResponse,
  Question,
  QuestionDetailResponse,
  QuestionsListResponse,
  QuestionsQueryParams,
  QuestionUpdatePayload,
} from '../models/question.model';
import {
  InsightsDashboardQueryParams,
  InsightsDashboardResponse,
  InsightsDashboardStatsResponse,
} from '../models/insights-dashboard.model';

@Injectable({
  providedIn: 'root',
})
export class QuestionsService {
  private api = inject(ApiService);

  /**
   * Get questions list with optional query parameters
   * @param params - Query parameters (page, page_size, status, search, etc.)
   */
  getQuestions(params?: QuestionsQueryParams): Observable<QuestionsListResponse> {
    return this.api.get(ENGAGEMENTS_ENDPOINTS.V2.ADMIN_QUESTIONS, {
      params: params || {},
    });
  }

  /**
   * Get question details by ID
   * @param id - Question ID
   */
  getQuestionById(id: number): Observable<QuestionDetailResponse> {
    return this.api.get(ENGAGEMENTS_ENDPOINTS.V2.ADMIN_QUESTION_DETAIL(id));
  }

  /**
   * Create new question
   * @param data - Question data
   */
  createQuestion(data: Partial<Question>): Observable<Question> {
    return this.api.post(ENGAGEMENTS_ENDPOINTS.V2.ADMIN_QUESTIONS, data);
  }

  /**
   * Update question by ID (also used for partial field updates, e.g. status/specialization)
   * @param id - Question ID
   * @param data - Fields to update
   */
  updateQuestion(id: number, data: QuestionUpdatePayload): Observable<QuestionDetailResponse> {
    return this.api.put(ENGAGEMENTS_ENDPOINTS.V2.ADMIN_QUESTION_DETAIL(id), data);
  }

  /**
   * Delete question by ID
   * @param id - Question ID
   */
  deleteQuestion(id: number): Observable<void> {
    return this.api.delete(ENGAGEMENTS_ENDPOINTS.V2.ADMIN_QUESTION_DETAIL(id));
  }

  /**
   * Get answers for a specific question
   * @param questionId - Question ID
   * @param params - Query parameters (page, page_size, etc.)
   */
  getQuestionAnswers(questionId: number, params?: QuestionAnswersQueryParams): Observable<QuestionAnswersResponse> {
    return this.api.get(ENGAGEMENTS_ENDPOINTS.V2.ADMIN_QUESTION_ANSWERS(questionId), {
      params: params || {},
    });
  }

  /**
   * Delete an answer by ID
   * @param answerId - Answer ID
   */
  deleteAnswer(answerId: number): Observable<void> {
    return this.api.delete(ENGAGEMENTS_ENDPOINTS.V2.ADMIN_ANSWER_DETAIL(answerId));
  }

  /**
   * Get the change log for a specific question
   * @param questionId - Question ID
   */
  getQuestionLogs(questionId: number): Observable<QuestionLogsResponse> {
    return this.api.get(ENGAGEMENTS_ENDPOINTS.V2.ADMIN_QUESTION_LOGS(questionId));
  }

  /**
   * Get dashboard summary stats (open/under review counts, active cohort)
   */
  getDashboardSummary(): Observable<DashboardSummaryResponse> {
    return this.api.get(ENGAGEMENTS_ENDPOINTS.V2.ADMIN_QUESTIONS_DASHBOARD_SUMMARY);
  }

  /**
   * Get the insights dashboard breakdowns (specialty distribution, monthly trend, top questions, top experts)
   * Filterable via status / date_range (+ date_from/date_to when custom) / specialization / expert.
   */
  getInsightsDashboard(params?: InsightsDashboardQueryParams): Observable<InsightsDashboardResponse> {
    return this.api.get(ENGAGEMENTS_ENDPOINTS.V2.ADMIN_QUESTIONS_INSIGHTS_DASHBOARD, {
      params: params || {},
    });
  }

  /**
   * Get the insights dashboard summary stats (totals, answered %, avg first response time).
   * Unfiltered/all-time — this endpoint takes no query params.
   */
  getInsightsDashboardStats(): Observable<InsightsDashboardStatsResponse> {
    return this.api.get(ENGAGEMENTS_ENDPOINTS.V2.ADMIN_QUESTIONS_INSIGHTS_DASHBOARD_STATS);
  }
}
